import type { Artwork } from '../types';
import type { AudioPlayer, AudioState } from './player';
import { silentWavDataUrl } from './unmute';

/** <audio> による再生。iOS は音量を変えられないのでフェードなし。消音スイッチには影響されない。 */
export class ElementPlayer implements AudioPlayer {
  private readonly el = document.createElement('audio');
  state: AudioState = 'idle';
  current: Artwork | null = null;
  muted = false;

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.el.muted = muted; // iOS でも muted は有効(volume は効かない)
  }

  constructor(private readonly log: (m: string) => void) {
    this.el.loop = true;
    this.el.preload = 'auto';
    this.el.setAttribute('playsinline', '');
  }

  unlock(): void {
    // 操作ハンドラ内で一度 play() した要素は、以後 src を変えても操作なしで play() できる
    this.el.src = silentWavDataUrl();
    void this.el.play().then(() => this.el.pause()).catch((e) => this.log(`audio unlock failed: ${e}`));
  }

  async preload(_a: Artwork): Promise<void> {
    /* ブラウザの preload="auto" に任せる */
  }

  async play(a: Artwork): Promise<void> {
    if (this.current?.id === a.id && !this.el.paused) return;
    this.el.src = a.audio_asset;
    this.el.volume = Math.min(1, a.volume_adjustment);
    this.el.muted = this.muted;
    try {
      await this.el.play();
    } catch (e) {
      this.state = 'blocked';
      throw e;
    }
    this.current = a;
    this.state = 'playing';
    this.log(`audio start (element): ${a.title}`);
  }

  async stop(): Promise<void> {
    this.el.pause();
    this.el.currentTime = 0;
    this.current = null;
    this.state = 'idle';
  }

  async pause(): Promise<void> {
    this.el.pause();
  }

  async resume(): Promise<boolean> {
    if (!this.current) return true;
    try {
      await this.el.play();
      this.state = 'playing';
      return true;
    } catch {
      this.state = 'blocked';
      return false;
    }
  }

  diagnostics(): string {
    return `audio element paused=${this.el.paused} t=${this.el.currentTime.toFixed(1)} ${this.state}${this.muted ? ' muted' : ''}`;
  }
}
